import { Button, IconButton } from '../../components/Button';
import { ProgressRing } from '../../components/ProgressRing';
import { useCountdown } from '../../hooks/useTimer';
import { useSound } from '../../hooks/useSound';
import type { BoardProps } from '../../engine/types';
import { cx } from '../../utils/classnames';
import type { TangleMove, TangleState } from './logic';

export function TangleBoard({ state, status, dispatch, feedback, startedAt }: BoardProps<TangleState, TangleMove>) {
  const playing = status === 'playing';
  const sound = useSound();
  // Derived from the session clock, so a reload or leaving the board can't reset the timer.
  const deadline = startedAt + state.limitMs;

  const remaining = useCountdown(deadline, playing, () =>
    dispatch({ type: 'timeout' }),
  );
  const seconds = Math.ceil(remaining / 1000);
  const low = remaining <= 10_000;
  const errorNonce = feedback?.tone === 'error' ? feedback.nonce : 0;

  const slots = Array.from({ length: state.length }, (_, p) => p);

  return (
    <div className="cv-board">
      <div className="cv-tangle">
        <ProgressRing
          value={remaining}
          max={state.limitMs}
          size={66}
          color={low ? 'var(--danger)' : 'var(--vine)'}
          ariaLabel={`${seconds} seconds remaining`}
        >
          <span className="cv-mono" style={{ fontWeight: 500, color: low ? 'var(--danger)' : 'var(--ink)' }}>
            {seconds}
          </span>
        </ProgressRing>

        <div
          key={`guess-${errorNonce}`}
          className={cx('cv-tangle__guess', errorNonce && 'cv-anim-shake')}
          data-solved={state.solved ? 'true' : undefined}
          aria-label="Your word"
        >
          {slots.map((p) => {
            const filled = p < state.guess.length;
            const ch = filled ? state.scrambled[state.guess[p]] : '';
            return (
              <button
                key={p}
                type="button"
                className="cv-tangle__slot"
                data-filled={filled ? 'true' : undefined}
                onClick={() => {
                  if (!filled || !playing) return;
                  sound.play('delete');
                  dispatch({ type: 'unplaceAt', slot: p });
                }}
                disabled={!filled || !playing}
                aria-label={filled ? `Remove ${ch}` : 'Empty slot'}
              >
                {ch.toUpperCase()}
              </button>
            );
          })}
        </div>

        <div className="cv-tangle__pool" role="group" aria-label="Scrambled letters">
          {state.scrambled.map((ch, i) => (
            <button
              key={i}
              type="button"
              className="cv-tangle__chip"
              data-used={state.guess.includes(i) ? 'true' : undefined}
              onClick={() => {
                sound.play('select');
                dispatch({ type: 'place', index: i });
              }}
              disabled={!playing || state.guess.includes(i)}
              aria-label={`Letter ${ch}`}
            >
              {ch.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="cv-hintbar">
          <IconButton
            icon="back"
            label="Remove last"
            onClick={() => {
              sound.play('delete');
              dispatch({ type: 'unplace' });
            }}
            disabled={!playing || state.guess.length === 0}
          />
          <Button
            variant="primary"
            onClick={() => {
              sound.play('submit');
              dispatch({ type: 'submit' });
            }}
            disabled={!playing || state.guess.length < state.length}
          >
            Enter
          </Button>
          <IconButton
            icon="shuffle"
            label="Shuffle letters"
            onClick={() => {
              sound.play('select');
              dispatch({ type: 'shuffle' });
            }}
            disabled={!playing}
          />
        </div>

        {state.revealedHint && (
          <span className="cv-chip cv-chip--seed">Starts with {state.answer[0].toUpperCase()}</span>
        )}
      </div>

      <div className="cv-board__status" data-tone={feedback?.tone === 'error' ? 'error' : undefined} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>
    </div>
  );
}
